import { Func } from '@skylab-shared/types';
import { Calc, MidpointRounding } from '@skylab/calculations/utils';

export const head = <T>([item, ..._]: T[]) => item;

export const last = <T>(arr: ArrayLike<T>) => arr[arr.length - 1];

export const init = <T>(arr: T[]) => arr.slice(0, arr.length - 1);

export const tail = <T>([, ...tail]: T[]) => tail;

export const adjacentItemsAsPairs = <T>(arr: T[]) => arr.slice(1).map((item, idx) => [item, arr[idx]]);

export const sum = <T>(arr: T[], f: Func<T, number>, precision?: number) =>
    Array.from(arr).reduce(precision ? (acc, item) => Calc.round(acc + f(item), precision, MidpointRounding.AwayFromZero) : (acc, item) => acc + f(item), 0);

    export const maxBy = <T>(arr: T[], f: Func<T, number>) => Array.from(arr).reduce((result, x) => f(x) > f(result) ? x : result);

export const groupBy = <T, TKey>(arr: T[], keySelector: Func<T, TKey>) => arr.reduce((acc, item) => {
    const key = keySelector(item);
    const group = acc.get(key);
    if (group) {
        group.push(item);
    } else {
        acc.set(key, [item]);
    }
    return acc;
}, new Map<TKey, T[]>());

export const flat = <T>(arr: T[][]) => (<T[]>[]).concat.apply([], arr);

export const unique = <T>(arr: T[]) => arr.filter((value, index, self) => self.indexOf(value) === index);

export const zip = <T>(arr1: T[], arr2: T[]): [first: T, second: T][] => arr1.map((item, idx) => [item, arr2[idx]]);

export const sameValues = <T>(arr1: T[], arr2: T[]) => arr1.every(v => arr2.includes(v));

export const hasDuplicates = <T>(arr: T[]) => new Set(arr).size !== arr.length;

export const hasMany = <T>(arr: T[], fn: Func<T, boolean>) => arr.filter(fn).length > 1;

export const chunk = <T>(arr: T[], size: number): T[][] => arr.length > size ? [arr.slice(0, size), ...chunk(arr.slice(size), size)] : [[...arr]];

export const moveInArray = <T>(arr: T[], fromIndex: number, toIndex: number): T[] => {
    if (toIndex === fromIndex || toIndex >= arr.length) return arr;

    const moveArr = arr[fromIndex];
    const movedNext = fromIndex < toIndex;

    return arr.reduce((res, next, index) => {
        if (index === fromIndex) return res;
        if (index === toIndex) return res.concat(
            movedNext ? [next, moveArr] : [moveArr, next]
        );

        return res.concat(next);
    }, [] as T[]);
};
